// The Big Knock's words (sprint 29 item 3; spec: studio/sprints/29/BIGKNOCK-SPEC.md, "Copy"). Pure, browser-safe, ASCII only.
// Short, plain, truthful. Points are points, not money: no prize words, no price, no wallet, and the share card carries no amounts at all.
// "Neighbour" is the game's word for a real person at the door, "alien" for the machine.

export const clock = seconds => `${Math.floor(Math.max(0, seconds) / 60)}:${String(Math.max(0, seconds) % 60).padStart(2, '0')}`;
export const groups = n => String(Math.max(0, Math.round(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const BIGKNOCK_COPY = {
  name: 'The Big Knock',
  // The hall banner, from 10 minutes before the doors open. One line and a button; it never covers the door's sum or the visitor's title bar.
  banner: { title: n => `Big Knock #${n}`, line: "One hard visitor, the same one for everyone. It's free to join.", button: 'See' },
  countdown: { toOpen: seconds => `Opens in ${clock(seconds)}`, open: seconds => `Doors close in ${clock(seconds)}`, deciding: seconds => `Reveal in ${clock(seconds)}`, now: 'Open now' },
  crowd: n => (n <= 0 ? 'Be the first at the door.' : n === 1 ? '1 at the door' : `${groups(n)} at the door`),
  join: 'Knock',
  rules: ['Chat with the visitor, wait 10 seconds, then open or keep shut.', 'Fast or slow, your chance is the same.', 'Everyone who calls it right goes into one draw for a big envelope.', "If you're fooled, you get keyring fragments instead."],
  truth: "Free to join, with points and marks only. There's nothing to buy.",
  // The draw is for players who have really played (sprint 31): n real doors before the knock. A right call without them still earns points.
  draw: n => `The draw is open to players who have played ${n} doors before.`,
  gate: "A call in the first 10 seconds, or without chatting, doesn't count.",
  waiting: seconds => `Calls are in. The reveal is in ${clock(seconds)}.`,
  late: 'The doors are shut. See you at the next Big Knock.',
  // The big reveal.
  reveal: {
    kind: kind => (kind === 'alien' ? 'It was a visitor.' : 'It was a neighbour.'),
    crowd: (right, real) => `${groups(right)} of ${groups(real)} got it right.`,
    drawn: w => `${w} big ${w === 1 ? 'envelope' : 'envelopes'} drawn.`,
    check: 'Check the draw',
  },
  // What you get, by what you did.
  you: {
    winner: 'You drew a big envelope.',
    right: "You called it, but you weren't drawn this time.",
    undrawn: n => `You called it. Play ${n} doors to join the draw.`,
    fooled: 'It fooled you.',
    notCounted: "Your call didn't count. Chat first, then wait 10 seconds.",
    timeout: 'You ran out of time.',
    absent: "You weren't at the door.",
  },
  gain: { big: points => `+${points} points and a big mark`, points: points => `+${points} points`, fragments: n => `+${n} keyring fragments`, keyring: (have, of) => `Keyring: ${have} of ${of}`,
    // what the keyring is for, in the free room too: nothing to sign in to (the fragments are kept under the browser); ten is MAIL.fragments.guarantee
    keyringUse: "At 10, your next right call opens an envelope. No need to sign in." },
  next: n => `Next Big Knock: #${n}. Watch the hall.`, // replaced by window.line on the reveal in the fix pass; kept until the client test moves
  // The next-event window (game/bigknock-window.js). True by construction: the start is 4 to 6 hours after the last one began, a public rule.
  window: {
    line: (n, lead, from, to) => `Next Big Knock:${n > 0 ? ' #' + n + ',' : ''} ${lead || ''}between ${from} and ${to}.`,
    due: (n, to) => `Next Big Knock:${n > 0 ? ' #' + n + ',' : ''} any time now, by ${to}.`,
    late: 'The next Big Knock is running late. Keep an eye on the hall.',
    rule: 'A Big Knock starts 4 to 6 hours after the last one.',
    // the page's own wording (game/bigknock-window.js windowStated): the day and the time zone are named, so the window is exact
    stated: { ahead: ({ n = 0, day = '', from = '', to = '', zone = '' } = {}) => `Next Big Knock:${n > 0 ? ' #' + n + ',' : ''} ${day}between ${from} and ${to} (${zone}).`,
      due: ({ n = 0, to = '', zone = '' } = {}) => `Next Big Knock:${n > 0 ? ' #' + n + ',' : ''} any time now, by ${to} (${zone}).` },
    day: d => (d === 1 ? 'tomorrow' : `in ${d} days`),
  },
  // After your call (the wait). The count is people who have called, whatever they called; it says nothing about right or wrong.
  wait: {
    open: seconds => `Your call is in. Doors close in ${clock(seconds)}.`,
    callsIn: (called, joined) => (joined > 0 && called >= joined ? `All ${groups(joined)} have called.` : `${groups(called)} of ${groups(joined)} have called.`),
    play: 'Play the door while you wait',
    locked: 'Your call is locked in. The reveal opens on its own.',
    ready: n => `Big Knock #${n} is revealed.`,
    see: 'See the reveal',
  },
  noNext: 'The next Big Knock is a few hours away.',
  again: 'Back to the door',
};

// The share card (item 59's cards): a headline and one line, never a name unless the player chose one, never a wallet, points, fragments or any amount.
export function shareText(n, outcome, { right = 0, real = 0 } = {}) {
  const head = { winner: `Big Knock #${n}. Called it and drew the big envelope.`, right: `Big Knock #${n}. Called it.`, fooled: `Big Knock #${n}. It fooled me.` }[outcome] ?? `Big Knock #${n}.`;
  const sub = real > 0 ? `${groups(right)} of ${groups(real)} got it right.` : '';
  return { head, sub, lines: sub ? [head, sub] : [head] };
}
